import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useGetRentManagerGeneralLedgerAccountsQuery } from "@redux/portfolios/portfoliosApi";

import { FormAutocomplete } from "@covertree/ui";

import { communityConfigurationCardSx } from "./communityConfigurationStyles";

import type {
  CommunityConfigurationFormGlAccount,
  CommunityConfigurationFormValues,
} from "./communitiesConfigurationForm";

interface RentManagerLocationsSectionProps {
  portfolioId: string;
}

interface RentManagerLocationGlAccountFieldProps {
  index: number;
  locationId: number;
  locationName: string;
  portfolioId: string;
}

function RentManagerLocationGlAccountField({
  index,
  locationId,
  locationName,
  portfolioId,
}: RentManagerLocationGlAccountFieldProps) {
  const { control } = useFormContext<CommunityConfigurationFormValues>();
  const {
    data: accounts,
    isError,
    isFetching,
  } = useGetRentManagerGeneralLedgerAccountsQuery({ locationId, portfolioId });

  const options = useMemo(
    () =>
      (accounts ?? [])
        .filter(({ isActive }) => isActive)
        .map(({ description, id, name }) => ({
          label: description ? `${name} · ${description}` : name,
          value: id,
        })),
    [accounts]
  );

  // A stored id arrives without a label; resolve it from the loaded accounts
  // so the input shows the account name, not an empty string. Memoised because
  // MUI Autocomplete resets its input whenever this callback changes identity.
  const getOptionLabel = useCallback(
    (option: CommunityConfigurationFormGlAccount) =>
      options.find(({ value }) => value === option.value)?.label ??
      option.label ??
      "",
    [options]
  );

  const placeholder = isFetching
    ? "Loading General Ledger accounts…"
    : isError
      ? "Unable to load General Ledger accounts"
      : "Search General Ledger accounts";

  return (
    <Grid item xs={12} md={6}>
      <FormAutocomplete
        control={control}
        dataTestId={`rent-manager-location-${index}-gl-account`}
        disabled={isFetching || isError}
        getOptionLabel={getOptionLabel}
        id={`rentManagerLocations-${index}-glAccount`}
        label={`${locationName} · BRL charge General Ledger account`}
        loading={isFetching}
        name={`rentManagerLocations.${index}.glAccount`}
        noOptionsText="No matching General Ledger accounts"
        options={options}
        placeholder={placeholder}
      />
    </Grid>
  );
}

/**
 * Lets the operator choose, per Rent Manager location, the General Ledger
 * account the BRL charge type is booked against. Rendered only for portfolios
 * integrated with Rent Manager; the selection is mandatory for activation.
 */
export function RentManagerLocationsSection({
  portfolioId,
}: RentManagerLocationsSectionProps) {
  const { control } = useFormContext<CommunityConfigurationFormValues>();
  const locations = useWatch({ control, name: "rentManagerLocations" }) ?? [];

  if (!locations.length) return null;

  return (
    <Stack sx={communityConfigurationCardSx} spacing={2}>
      <Typography variant="h4">Rent Manager billing</Typography>
      <Typography color="text.secondary">
        Pick the General Ledger account the BRL insurance charge type is added
        to in each Rent Manager location. This is required before the portfolio
        can be activated.
      </Typography>
      {locations.some(({ glAccount }) => glAccount.value === "") && (
        <Alert severity="warning">
          Every Rent Manager location needs a General Ledger account.
        </Alert>
      )}
      <Grid container spacing={2}>
        {locations.map((location, index) => (
          <RentManagerLocationGlAccountField
            index={index}
            key={location.id}
            locationId={location.id}
            locationName={location.name}
            portfolioId={portfolioId}
          />
        ))}
      </Grid>
    </Stack>
  );
}
